import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDisplayDate } from 'i18n/calendarLabels';
import routes from 'routes';
import {
  disconnectProviderRequest,
  fetchConnectUrlRequest,
} from 'store/account/actions';
import { AccountType, Provider } from 'store/api/enums';
import { ACCESS_RIGHTS_DRAWER_WIDTH } from 'store/app/constants';
import { turnDrawerOff, turnDrawerOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { getAuthUser } from 'store/auth/selectors';
import { getOrgMappedOrganisations } from 'store/organisation/selectors';
import {
  fetchUserHistoryRequest,
  fetchUserRightsRequest,
  setAllUserRightsRequest,
  setUserRightsRequest,
} from 'store/user/actions';
import {
  getUserHistory,
  getUserLoading,
  getUserMembers,
  getUserRights,
} from 'store/user/selectors';
import { Rights } from 'components/UsersComponent/rights';
import { TablePermission } from 'store/api/enums';
import { UserRights } from 'store/user/models';
import ImgWithQboSignIn from 'assets/images/qb-icon.png';
import ImgWithXeroSignIn from 'assets/images/xero_icon.png';
import ImgWithZohoSignIn from 'assets/images/zoho_icon.png';
import { PwaRow, PwaSection, PwaTopBar } from './ui';

export const PwaSecurityPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(getAuthUser);
  const history = useSelector(getUserHistory);
  const rights = useSelector(getUserRights);
  const loading = useSelector(getUserLoading);
  const mappedOrganisations = useSelector(getOrgMappedOrganisations);
  const members = useSelector(getUserMembers);
  const [rightsOpen, setRightsOpen] = React.useState(false);

  React.useEffect(() => {
    if (user?.userUuid) dispatch(fetchUserHistoryRequest(user.userUuid));
  }, [dispatch, user?.userUuid]);

  const latest = history?.[0];
  const signedInLabel = latest?.loginTimestamp
    ? formatDisplayDate(
        latest.loginTimestamp,
        'D MMM YYYY, HH:mm',
        i18n.language
      )
    : user?.lastLogin
      ? formatDisplayDate(user.lastLogin, 'D MMM YYYY, HH:mm', i18n.language)
      : '—';

  const canManageRights =
    !!user?.accountType &&
    AccountType[user.accountType] !== AccountType.STAFF &&
    AccountType[user.accountType] !== AccountType.CLIENT;

  const saveUserRights = (
    userRights: UserRights,
    tablePermission: TablePermission
  ) => {
    const accountUuid = userRights.accountUuid;
    const userToModify = members?.find((m) => m.accountUuid === accountUuid);
    dispatch(
      setUserRightsRequest(
        userRights,
        tablePermission,
        userToModify || undefined
      )
    );
  };

  const saveAllUserRights = (
    userRights: UserRights[],
    tablePermission: TablePermission
  ) => {
    const accountUuid = userRights[0]?.accountUuid;
    const userToModify = members?.find((m) => m.accountUuid === accountUuid);
    dispatch(
      setAllUserRightsRequest(
        userRights,
        tablePermission,
        userToModify || undefined
      )
    );
  };

  React.useEffect(() => {
    if (!rightsOpen || !rights) return;
    const modalData: DrawerData = {
      type: DrawerType.RIGHTS,
      title: t('users.access-rights'),
      width: ACCESS_RIGHTS_DRAWER_WIDTH,
      component: !loading && rights && (
        <Rights
          loading={loading}
          rights={rights}
          saveUserRights={saveUserRights}
          saveAllUserRights={saveAllUserRights}
          entities={mappedOrganisations}
          onClose={() => {
            setRightsOpen(false);
            dispatch(turnDrawerOff());
          }}
        />
      ),
    };
    dispatch(turnDrawerOn(modalData));
    setRightsOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rights, rightsOpen]);

  const openAccessRights = () => {
    if (!user?.accountUuid) return;
    setRightsOpen(true);
    dispatch(fetchUserRightsRequest(user.accountUuid));
  };

  const connect = (provider: Provider, path: string) => {
    const providerFormattedName = t(`common.${provider?.toLowerCase()}`, {
      defaultValue: provider,
    });
    dispatch(fetchConnectUrlRequest(provider, path, providerFormattedName));
  };

  const disconnect = (provider: Provider) => {
    dispatch(
      disconnectProviderRequest(
        provider,
        t(`common.${provider?.toLowerCase()}`, { defaultValue: provider })
      )
    );
  };

  const linked = [
    {
      id: 'xero' as const,
      name: t('common.xero', { defaultValue: 'Xero' }),
      logo: ImgWithXeroSignIn,
      on: !!user?.xeroEnabled,
      connect: () => connect(Provider.XERO, 'linkxero'),
      disconnect: () => disconnect(Provider.XERO),
    },
    {
      id: 'qbo' as const,
      name: t('common.quickbooks', { defaultValue: 'QuickBooks Online' }),
      logo: ImgWithQboSignIn,
      on: !!user?.quickbooksEnabled,
      connect: () => connect(Provider.QUICKBOOKS, 'linkquickbook'),
      disconnect: () => disconnect(Provider.QUICKBOOKS),
    },
    {
      id: 'zoho' as const,
      name: t('common.zoho', { defaultValue: 'Zoho Books' }),
      logo: ImgWithZohoSignIn,
      on: !!user?.zohoEnabled,
      connect: () => connect(Provider.ZOHO, 'linkzoho'),
      disconnect: () => disconnect(Provider.ZOHO),
    },
  ];

  const goBack = () => navigate(routes.account.root);

  return (
    <div className="pwa-more-page" data-shell="pwa">
      <PwaTopBar
        title={t('users.account-security', {
          defaultValue: 'Account & security',
        })}
        onBack={goBack}
        backLabel={t('common.back')}
      />
      <main className="pwa-main">
        <div style={{ height: 16 }} />
        <PwaSection
          title={t('users.current-session', { defaultValue: 'Current session' })}
        >
          <div className="pwa-list">
            <div className="pwa-row-block">
              <dl className="pwa-kv" style={{ margin: 0 }}>
                <div>
                  <dt>{t('users.ip-address', { defaultValue: 'IP address' })}</dt>
                  <dd className="pwa-mono">{latest?.loginIP || '—'}</dd>
                </div>
                <div>
                  <dt>{t('users.signed-in', { defaultValue: 'Signed in' })}</dt>
                  <dd>{signedInLabel}</dd>
                </div>
              </dl>
            </div>
            <PwaRow
              icon="history"
              title={t('users.login-history', { defaultValue: 'Login history' })}
              chevron
              onClick={() =>
                navigate(`${routes.account.root}?view=history`)
              }
            />
          </div>
        </PwaSection>

        {canManageRights ? (
          <PwaSection
            title={t('users.permissions', { defaultValue: 'Permissions' })}
          >
            <div className="pwa-list">
              <PwaRow
                icon="key"
                title={t('users.access-rights')}
                sub={t('users.access-rights-sub', {
                  defaultValue: 'Choose which entities this user can see',
                })}
                chevron
                wrap
                onClick={openAccessRights}
              />
            </div>
          </PwaSection>
        ) : null}

        <PwaSection
          title={t('users.sign-in-with', { defaultValue: 'Sign in with' })}
        >
          <div className="pwa-list">
            {linked.map((l) => (
              <div key={l.id} className="pwa-row static">
                <img
                  className="pwa-provider-logo"
                  src={l.logo}
                  alt=""
                  width={40}
                  height={40}
                />
                <span className="pwa-row-body">
                  <span className="pwa-row-title">{l.name}</span>
                  <span className="pwa-row-sub">
                    {l.on
                      ? t('common.linked', { defaultValue: 'Linked' })
                      : t('common.not-linked', { defaultValue: 'Not linked' })}
                  </span>
                </span>
                {l.on ? (
                  <button
                    type="button"
                    className="pwa-btn secondary sm"
                    onClick={l.disconnect}
                  >
                    {t('common.unlink', { defaultValue: 'Unlink' })}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="pwa-btn tonal sm"
                    onClick={l.connect}
                  >
                    {t('common.link', { defaultValue: 'Link' })}
                  </button>
                )}
              </div>
            ))}
          </div>
          <p className="pwa-row-meta" style={{ margin: '0 4px' }}>
            {t('users.link-provider-help', {
              defaultValue:
                'Linking a provider lets you sign in with it and adds an extra security check.',
            })}
          </p>
        </PwaSection>
      </main>
    </div>
  );
};

export default PwaSecurityPage;
